/**
 * ============================================================================
 * WALLET PAGE
 * ============================================================================
 *
 * Green/black theme matching sidebar and dashboard – hero, themed tabs, cards
 */

import React, { useEffect, useState, useRef } from 'react';
import { useWallet } from '../hooks/useWallet';
import { useWalletStore } from '../store';
import useAuthStore from '../store/authStore';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import WalletBalance from '../components/wallet/WalletBalance';
import TransactionHistory from '../components/wallet/TransactionHistory';
import WithdrawalForm from '../components/wallet/WithdrawalForm';
import WalletStats from '../components/wallet/WalletStats';
import WithdrawalHistory from '../components/wallet/WithdrawalHistory';
import Spinner from '../components/common/Spinner';
import '../styles/landing.css';
import '../styles/animations.css';
import '../styles/global.css';

const Wallet = () => {
  const { loadWallet, loadTransactions, loadWithdrawals, loading } = useWallet();
  const { wallet, transactions, withdrawals } = useWalletStore();
  const accessToken = useAuthStore((s) => s.accessToken);
  const { showError } = useToast();
  const { language } = useI18n();
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [contentInView, setContentInView] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!accessToken) return;
    const loadData = async () => {
      try {
        await Promise.all([
          loadWallet(),
          loadTransactions(),
          loadWithdrawals(),
        ]);
      } catch (error) {
        showError('Failed to load wallet data');
        console.error('Load wallet error:', error);
      }
    };

    loadData();
  }, [accessToken, loadWallet, loadTransactions, loadWithdrawals, showError]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => e.isIntersecting && setContentInView(true)); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab]);

  const tabs = [
    {
      id: 'overview',
      label: language === 'fr' ? "Vue d'ensemble" : 'Overview',
      icon: '📊',
      desc: language === 'fr' ? 'Solde & stats' : 'Balance & stats',
    },
    {
      id: 'transactions',
      label: language === 'fr' ? 'Transactions' : 'Transactions',
      icon: '💳',
      desc: language === 'fr' ? 'Historique' : 'History',
    },
    {
      id: 'withdrawals',
      label: language === 'fr' ? 'Retraits' : 'Withdrawals',
      icon: '💸',
      desc: language === 'fr' ? 'Demandes & historique' : 'Requests & history',
    },
  ];

  if (loading && !wallet) {
    return (
      <UserDashboardLayout>
        <div className="wallet-wt-loading dashboard-wt">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement du portefeuille...' : 'Loading wallet...'}
            </p>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="wallet-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />

          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              💰 {language === 'fr' ? 'Portefeuille' : 'Wallet'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Gérez votre solde, transactions et retraits'
                : 'Manage your balance, transactions, and withdrawals'}
            </p>
          </div>
        </section>

        {/* Tabs */}
        <section className="settings-wt-section profile-wt-tabs-wrap">
          <div className="settings-wt-section-inner">
            <div className="profile-wt-tabs" role="tablist">
              {tabs.map((tab, i) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`profile-wt-tab ${activeTab === tab.id ? 'profile-wt-tab-active' : ''}`}
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <span className="profile-wt-tab-icon">{tab.icon}</span>
                  <span className="profile-wt-tab-label">{tab.label}</span>
                  <span className="profile-wt-tab-desc">{tab.desc}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Tab content */}
        <section
          className={`settings-wt-section dashboard-wt-section-alt wallet-wt-content ${contentInView ? 'is-inview' : ''}`}
          ref={contentRef}
        >
          <div className="settings-wt-section-inner">
            {activeTab === 'overview' && (
              <div className="wallet-wt-overview">
                <div className="dashboard-wt-transactions" style={{ animationDelay: '0s' }}>
                  <div className="dashboard-wt-transaction-wrapper">
                    <WalletBalance />
                  </div>
                </div>

                <div className="dashboard-wt-card wallet-wt-card-stats">
                  <div className="dashboard-wt-card-header">
                    <h2 className="dashboard-wt-card-title">
                      {language === 'fr' ? 'Statistiques' : 'Statistics'}
                    </h2>
                  </div>
                  <div className="dashboard-wt-card-content">
                    <WalletStats />
                  </div>
                </div>

                <div className="dashboard-wt-card wallet-wt-card-withdraw">
                  {!showWithdrawalForm ? (
                    <div className="dashboard-wt-card-content">
                      <button
                        type="button"
                        className="dashboard-wt-btn-primary dashboard-wt-btn-large w-full"
                        onClick={() => setShowWithdrawalForm(true)}
                      >
                        <span className="dashboard-wt-btn-icon">💸</span>
                        {language === 'fr' ? 'Demander un retrait' : 'Request Withdrawal'} →
                      </button>
                    </div>
                  ) : (
                    <div className="dashboard-wt-card-content">
                      <div className="wallet-wt-form-header">
                        <h3 className="dashboard-wt-card-title">
                          {language === 'fr' ? 'Nouveau retrait' : 'New Withdrawal'}
                        </h3>
                        <button
                          type="button"
                          className="wallet-wt-close-btn"
                          onClick={() => setShowWithdrawalForm(false)}
                          aria-label={language === 'fr' ? 'Fermer' : 'Close'}
                        >
                          ✕
                        </button>
                      </div>
                      <WithdrawalForm
                        onSuccess={() => {
                          setShowWithdrawalForm(false);
                          loadWithdrawals();
                        }}
                        onCancel={() => setShowWithdrawalForm(false)}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'transactions' && (
              <div className="dashboard-wt-card">
                <div className="dashboard-wt-card-header">
                  <h2 className="dashboard-wt-card-title flex items-center gap-2">
                    <span className="text-2xl">📊</span>
                    {language === 'fr' ? 'Historique des transactions' : 'Transaction History'}
                  </h2>
                </div>
                <div className="dashboard-wt-card-content">
                  <TransactionHistory />
                </div>
              </div>
            )}

            {activeTab === 'withdrawals' && (
              <div className="wallet-wt-withdrawals">
                {/* Balance strip + CTA when form is closed */}
                {!showWithdrawalForm && (
                  <div className="dashboard-wt-card wallet-wt-withdrawals-strip mb-6">
                    <div className="wallet-wt-strip-inner">
                      <div className="wallet-wt-strip-balance">
                        <span className="dashboard-wt-card-desc">
                          {language === 'fr' ? 'Solde disponible' : 'Available balance'}
                        </span>
                        <span className="wallet-wt-strip-amount">
                          {wallet?.currency === 'XAF' || !wallet?.currency
                            ? (wallet?.availableBalance ?? 0).toLocaleString('fr-FR') + ' FCFA'
                            : (wallet?.availableBalance ?? 0).toLocaleString() + ' ' + (wallet?.currency || 'XAF')}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="dashboard-wt-btn-primary dashboard-wt-btn-large"
                        onClick={() => setShowWithdrawalForm(true)}
                      >
                        <span className="dashboard-wt-btn-icon">💸</span>
                        {language === 'fr' ? 'Nouveau retrait' : 'New Withdrawal'}
                      </button>
                    </div>
                  </div>
                )}

                {showWithdrawalForm ? (
                  <div className="dashboard-wt-card">
                    <div className="dashboard-wt-card-header wallet-wt-form-header">
                      <h2 className="dashboard-wt-card-title">
                        {language === 'fr' ? 'Nouveau retrait' : 'New Withdrawal'}
                      </h2>
                      <button
                        type="button"
                        className="wallet-wt-close-btn"
                        onClick={() => setShowWithdrawalForm(false)}
                        aria-label={language === 'fr' ? 'Fermer' : 'Close'}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="dashboard-wt-card-content">
                      <WithdrawalForm
                        onSuccess={() => {
                          setShowWithdrawalForm(false);
                          loadWithdrawals();
                        }}
                        onCancel={() => setShowWithdrawalForm(false)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="dashboard-wt-card">
                    <div className="dashboard-wt-card-header">
                      <h2 className="dashboard-wt-card-title flex items-center gap-2">
                        <span className="text-xl">📋</span>
                        {language === 'fr' ? 'Historique des retraits' : 'Withdrawal History'}
                      </h2>
                    </div>
                    <div className="dashboard-wt-card-content">
                      <WithdrawalHistory />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </UserDashboardLayout>
  );
};

export default Wallet;
